import { useInput } from 'react-admin';
import {
  Stack, Box, Typography, TextField, IconButton, Button, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../theme.js';

const DEFAULT_VALUE = {
  eyebrow: '',
  rawTitle: '',
  body: '',
  actions: [],
};

const BUTTON_STYLES = [
  { id: 'gold',    name: 'Gold (primary)' },
  { id: 'outline', name: 'Outline (secondary)' },
];

/**
 * Editor for the homepage "Start the journey" band stored at
 * SiteSettings.journeyBand. The component renders a row for each top-level
 * field and an Actions sub-editor for the CTA button list.
 *
 * The body text supports a `{schoolName}` token that is substituted on
 * the public site so the band stays in sync with the school name.
 */
export function JourneyBandEditor({ source = 'journeyBand' }) {
  const { field } = useInput({ source });
  const value = field.value && typeof field.value === 'object' ? field.value : DEFAULT_VALUE;
  const actions = Array.isArray(value.actions) ? value.actions : [];

  const patch = (p) => field.onChange({ ...value, ...p });
  const patchActions = (next) => patch({ actions: next });

  const setAction = (i, p) => patchActions(actions.map((a, idx) => (idx === i ? { ...a, ...p } : a)));
  const addAction = () => patchActions([...actions, { label: '', to: '/contact', style: 'outline' }]);
  const removeAction = (i) => patchActions(actions.filter((_, idx) => idx !== i));
  const moveAction = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= actions.length) return;
    const next = actions.slice();
    [next[i], next[j]] = [next[j], next[i]];
    patchActions(next);
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6 }}>
        Bottom-of-homepage CTA band. The body text supports a <code>{'{schoolName}'}</code> token that gets replaced with the school name set above.
      </Typography>

      <TextField
        size="small"
        label="Eyebrow"
        value={value.eyebrow || ''}
        onChange={(e) => patch({ eyebrow: e.target.value })}
        helperText='Small caps label above the title. E.g. "Start the journey".'
        fullWidth
      />

      <TextField
        size="small"
        label="Title"
        value={value.rawTitle || ''}
        onChange={(e) => patch({ rawTitle: e.target.value })}
        helperText='Italic words wrapped in <em>…</em>. E.g. "An education for <em>life</em>".'
        fullWidth
      />

      <TextField
        size="small"
        label="Body"
        value={value.body || ''}
        onChange={(e) => patch({ body: e.target.value })}
        multiline
        minRows={2}
        fullWidth
        helperText='Use {schoolName} to insert the school name dynamically.'
      />

      <Box sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.ink, mb: 0.5 }}>
          Buttons
        </Typography>
        <Typography variant="caption" sx={{ color: tokens.ink3, fontSize: 12, lineHeight: 1.6, display: 'block', mb: 1.5 }}>
          Up to ~3 CTAs shown side-by-side. Use a relative path (<code>/contact</code>) or a full URL.
        </Typography>

        <Stack spacing={1}>
          {actions.map((a, i) => (
            <Box
              key={i}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 160px auto' },
                gap: 1.5,
                alignItems: 'center',
                p: 1.5,
                border: `1px solid ${tokens.line}`,
                borderRadius: 1,
                background: tokens.paper,
              }}
            >
              <TextField
                size="small"
                label="Label"
                value={a.label || ''}
                onChange={(e) => setAction(i, { label: e.target.value })}
              />
              <TextField
                size="small"
                label="Link"
                value={a.to || ''}
                onChange={(e) => setAction(i, { to: e.target.value })}
                placeholder="/contact"
              />
              <TextField
                size="small"
                select
                label="Style"
                value={a.style || 'outline'}
                onChange={(e) => setAction(i, { style: e.target.value })}
              >
                {BUTTON_STYLES.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </TextField>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => moveAction(i, -1)} disabled={i === 0}>
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => moveAction(i, 1)} disabled={i === actions.length - 1}>
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => removeAction(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Box>
          ))}
        </Stack>

        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addAction} sx={{ mt: 1.5 }}>
          Add button
        </Button>
      </Box>
    </Stack>
  );
}
